import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PasswordInput from '../components/PasswordInput';
import PhaseDefaultsSection from '../components/settings/PhaseDefaultsSection';
import FigmaConnectionSection from '../components/settings/FigmaConnectionSection';
import LegalLinks from '../components/LegalLinks';
import { useTheme } from '../contexts/ThemeContext';
import { apiFetch } from '../lib/api';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';
import { useInternalAdminRole } from '../hooks/useInternalAdminRole';
import { isSectionVisible, getAccountSectionLabel } from '../lib/tier-features';
import { getOnboardingPathForTier } from '../lib/tier';
import PlanCard from '../components/PlanCard';
import ProceedAtRiskSetting from '../components/ProceedAtRiskSetting'; // #3188 APPROVE.5
import { PHASE_LABELS, PHASE_ORDER } from './settingsPhaseLabels';
import { LANDING_PAGES, LANDING_LABELS } from '../lib/landingRoute';
import { isFeatureEnabled } from '../config/featureFlags'; // PKGV.D4 (#804)

const themeOptions = [
  { value: 'light', label: 'Light' },
  { value: 'dark',  label: 'Dark' },
  { value: 'system', label: 'System' },
] as const;

interface FrequencyOption {
  value: string;
  label: string;
  description: string;
}

interface UserNotifPref {
  id: string | null;
  channel: string;
  frequency: string;
  digestTime: string | null;
  enabled: boolean;
  // WEB_PUSH rows carry the agent-pause debounce window (30 or 60). Null
  // for non-WEB_PUSH rows — the API sets it that way so the shape is
  // uniform across channels.
  pushDebounceSeconds?: number | null;
}

interface WhatsAppChannelStatus {
  configured: boolean;
  enabled: boolean;
  templatesApproved: boolean;
  phoneNumberId: string | null;
  wabaId: string | null;
}

interface WebhookChannelStatus {
  configured: boolean;
  enabled: boolean;
  webhookUrl: string | null;
  verifiedAt: string | null;
}

interface ChecklistTemplate {
  id: string;
  name: string;
  phaseId: string | null;
  checklistType: string;
  description: string | null;
  isOrganisationMinimum: boolean;
}

const TYPE_BADGE: Record<string, { label: string; color: string }> = {
  ENTRY: { label: 'Entry', color: '#10b981' },
  EXIT:  { label: 'Exit',  color: '#ef4444' },
  GENERAL: { label: 'General', color: '#6b7280' },
};

export default function SettingsPage() {
  const { preference, setPreference } = useTheme();
  const { myTenant } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);
  // Per-tier Settings UX (issue #779): tier + isPartner drive section visibility
  // and the Account/Settings label. Reads from useMyTenant — falls back to the
  // team-tier defaults when myTenant hasn't loaded yet.
  const tier = myTenant?.subscriptionTier;
  const isPartner = myTenant?.isPartner === true;
  const accountLabel = getAccountSectionLabel(tier, 'sidebar');  // 'Account' or 'Settings'
  const orgLabel     = getAccountSectionLabel(tier, 'page');     // 'Account' or 'Organisation'
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<{ templates: number; sections: number; items: number } | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  // #1796/#1806 — seeding global templates is SUPER_ADMIN-only on the server.
  // `canWrite` is display-only (hides a button that would 403), NOT an authorization decision.
  const { canWrite: isSuperAdmin, loading: adminRoleLoading } = useInternalAdminRole();
  const [notifPrefs, setNotifPrefs] = useState<UserNotifPref[]>([]);
  const [frequencyOptions, setFrequencyOptions] = useState<FrequencyOption[]>([]);
  const [notifSaving, setNotifSaving] = useState(false);

  // DASH.C1 (#825) — default landing page preference (self-scoped).
  const [landingPref, setLandingPref] = useState<string>('dashboard');
  const [landingSaving, setLandingSaving] = useState(false);
  // ACT.A8 (#1709) — Settings → Onboarding → Restart.
  const [onboardingRestarting, setOnboardingRestarting] = useState(false);

  // Escalation rules (tenant-level)
  const [escalationRules, setEscalationRules] = useState<Record<string, any>[]>([]);
  // #3188 — the effective values; only the proceed-at-risk toggle reads them.
  const [escalationEffective, setEscalationEffective] = useState<Record<string, any> | null>(null);
  const [escalationLoading, setEscalationLoading] = useState(true);
  const [escalationSaving, setEscalationSaving] = useState(false);
  // Pending day-threshold edits keyed by `${ruleType}:${dayField}` → integer
  const [escalationDayEdits, setEscalationDayEdits] = useState<Record<string, number>>({});
  const [escalationSaveError, setEscalationSaveError] = useState<string | null>(null);
  const [escalationSavedAt, setEscalationSavedAt] = useState<number | null>(null);

  // User phone number for WhatsApp
  const [userPhone, setUserPhone] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneLoaded, setPhoneLoaded] = useState(false);

  // WhatsApp channel state
  const [waStatus, setWaStatus] = useState<WhatsAppChannelStatus | null>(null);
  const [waLoading, setWaLoading] = useState(true);
  const [waSaving, setWaSaving] = useState(false);
  const [waTestResult, setWaTestResult] = useState<{ connected: boolean; message: string } | null>(null);
  const [waForm, setWaForm] = useState({ phoneNumberId: '', wabaId: '', accessToken: '' });

  // Teams channel state
  const [teamsStatus, setTeamsStatus] = useState<WebhookChannelStatus | null>(null);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsSaving, setTeamsSaving] = useState(false);
  const [teamsTestResult, setTeamsTestResult] = useState<{ connected: boolean; message: string } | null>(null);
  const [teamsWebhookUrl, setTeamsWebhookUrl] = useState('');
  const [teamsError, setTeamsError] = useState<string | null>(null);

  // Slack channel state
  const [slackStatus, setSlackStatus] = useState<WebhookChannelStatus | null>(null);
  const [slackLoading, setSlackLoading] = useState(true);
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackTestResult, setSlackTestResult] = useState<{ connected: boolean; message: string } | null>(null);
  const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
  const [slackError, setSlackError] = useState<string | null>(null);

  // Tool integrations (Jira / ADO)
  interface ToolConnection {
    id: string;
    toolType: string;
    displayName: string;
    status: string;
    lastSyncAt: string | null;
    createdAt: string;
  }
  const [toolConnections, setToolConnections] = useState<ToolConnection[]>([]);
  const [toolsLoading, setToolsLoading] = useState(true);

  useEffect(() => {
    apiFetch<ChecklistTemplate[]>('/checklist-templates')
      .then(setTemplates)
      .catch(() => {})
      .finally(() => setTemplatesLoading(false));

    apiFetch<UserNotifPref[]>('/notifications/preferences')
      .then(setNotifPrefs)
      .catch(() => {});

    apiFetch<{ frequencies: FrequencyOption[] }>('/notifications/preferences/defaults')
      .then(r => setFrequencyOptions(r.frequencies))
      .catch(() => {});

    apiFetch<{ phone: string | null }>('/notifications/preferences/phone')
      .then(r => { if (r.phone) setUserPhone(r.phone); setPhoneLoaded(true); })
      .catch(() => setPhoneLoaded(true));

    // DASH.C1 (#825) — current default landing page (self-scoped GET).
    apiFetch<{ default_landing_page: string }>('/users/me/preferences')
      .then(r => setLandingPref(r.default_landing_page))
      .catch(() => {});

    if (tenantId) {
      apiFetch<ToolConnection[]>('/tools/connections')
        .then(setToolConnections)
        .catch(() => {})
        .finally(() => setToolsLoading(false));
    } else {
      setToolsLoading(false);
    }
  }, [tenantId]);

  // ── Escalation rules ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantId || !isAdmin) return;
    apiFetch<{ rules: Record<string, any>[]; effective?: Record<string, any> }>(`/tenants/${tenantId}/escalation-rules`)
      .then(r => { setEscalationRules(r.rules); setEscalationEffective(r.effective ?? null); })
      .catch(() => {})
      .finally(() => setEscalationLoading(false));
  }, [tenantId, isAdmin]);

  // #3188 APPROVE.5 — set (true/false) or clear (null, inherit) the tenant's
  // "Allow proceed at risk" setting, then re-read the rules: the effective
  // value can come from the partner or platform level.
  const handleAllowProceedAtRisk = async (value: boolean | null) => {
    if (!tenantId) return;
    setEscalationSaving(true);
    try {
      await apiFetch(`/tenants/${tenantId}/escalation-rules`, {
        method: 'PATCH',
        body: JSON.stringify({ ruleType: 'CLIENT_APPROVAL_OVERDUE', allowProceedAtRisk: value }),
      });
      const r = await apiFetch<{ rules: Record<string, any>[]; effective?: Record<string, any> }>(`/tenants/${tenantId}/escalation-rules`);
      setEscalationRules(r.rules);
      setEscalationEffective(r.effective ?? null);
    } catch { /* ignore, as the other toggles */ } finally { setEscalationSaving(false); }
  };

  const handleEscalationToggle = async (ruleType: string, field: string, value: boolean) => {
    if (!tenantId) return;
    setEscalationSaving(true);
    try {
      await apiFetch(`/tenants/${tenantId}/escalation-rules`, {
        method: 'PATCH',
        body: JSON.stringify({ ruleType, [field]: value }),
      });
      setEscalationRules(prev => prev.map(r =>
        r.ruleType === ruleType ? { ...r, [field]: value } : r
      ));
    } catch { /* ignore */ } finally { setEscalationSaving(false); }
  };

  // Platform defaults (mirror apps/api/src/config/escalation-defaults.ts)
  const ESCALATION_PLATFORM_DEFAULTS = {
    stallNotifyDays: 5, stallUrgentDays: 7, stallPartnerDays: 14,
    approvalNotifyDays: 3, approvalUrgentDays: 5, approvalRenewalDays: 7, approvalEscalateDays: 10,
  } as const;

  const effectiveDay = (rule: Record<string, any>, field: keyof typeof ESCALATION_PLATFORM_DEFAULTS): number => {
    const editKey = `${rule.ruleType}:${field}`;
    if (editKey in escalationDayEdits) return escalationDayEdits[editKey];
    if (typeof rule[field] === 'number') return rule[field];
    return ESCALATION_PLATFORM_DEFAULTS[field];
  };

  const handleDayChange = (ruleType: string, field: string, raw: string) => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    setEscalationDayEdits(prev => ({ ...prev, [`${ruleType}:${field}`]: n }));
    setEscalationSaveError(null);
    setEscalationSavedAt(null);
  };

  const handleEscalationSave = async () => {
    if (!tenantId) return;
    setEscalationSaveError(null);
    setEscalationSavedAt(null);

    // Group pending edits by ruleType
    const byRule: Record<string, Record<string, number>> = {};
    for (const [key, value] of Object.entries(escalationDayEdits)) {
      const [ruleType, field] = key.split(':');
      if (value < 1 || value > 30) {
        setEscalationSaveError(`${field} must be between 1 and 30`);
        return;
      }
      (byRule[ruleType] ??= {})[field] = value;
    }

    if (Object.keys(byRule).length === 0) return;

    // Client-side ordering check using the effective (edited-or-stored-or-default) value per field.
    // Stall chain: notify < urgent < partner (3 values).
    // Approval chain: notify < urgent < renewal < escalate (4 values — renewal sits between urgent and escalate).
    const stallOrder: Array<keyof typeof ESCALATION_PLATFORM_DEFAULTS> = ['stallNotifyDays', 'stallUrgentDays', 'stallPartnerDays'];

    for (const [ruleType] of Object.entries(byRule)) {
      const rule = escalationRules.find(r => r.ruleType === ruleType);
      if (!rule) continue;
      if (ruleType === 'PHASE_STALL') {
        const [nf, uf, pf] = stallOrder;
        const n = effectiveDay(rule, nf), u = effectiveDay(rule, uf), p = effectiveDay(rule, pf);
        if (!(n < u)) {
          setEscalationSaveError(`Notify day (${n}) must be less than Urgent day (${u})`);
          return;
        }
        if (!(u < p)) {
          setEscalationSaveError(`Urgent day (${u}) must be less than Partner day (${p})`);
          return;
        }
      } else {
        const n = effectiveDay(rule, 'approvalNotifyDays');
        const u = effectiveDay(rule, 'approvalUrgentDays');
        const r = effectiveDay(rule, 'approvalRenewalDays');
        const e = effectiveDay(rule, 'approvalEscalateDays');
        if (!(n < u)) {
          setEscalationSaveError(`Notify day (${n}) must be less than Urgent day (${u})`);
          return;
        }
        if (!(u < r && r < e)) {
          setEscalationSaveError(`Renewal day must be between urgent day and partner alert day`);
          return;
        }
      }
    }

    setEscalationSaving(true);
    try {
      for (const [ruleType, updates] of Object.entries(byRule)) {
        await apiFetch(`/tenants/${tenantId}/escalation-rules`, {
          method: 'PATCH',
          body: JSON.stringify({ ruleType, ...updates }),
        });
        setEscalationRules(prev => prev.map(r => r.ruleType === ruleType ? { ...r, ...updates } : r));
      }
      setEscalationDayEdits({});
      setEscalationSavedAt(Date.now());
    } catch (e: any) {
      setEscalationSaveError(e?.message ?? 'Failed to save escalation settings');
    } finally {
      setEscalationSaving(false);
    }
  };

  // ── WhatsApp channel ──────────────────────────────────────────────────────
  const fetchWaStatus = useCallback(() => {
    if (!tenantId) return;
    setWaLoading(true);
    apiFetch<WhatsAppChannelStatus>(`/tenants/${tenantId}/notification-channels/whatsapp`)
      .then(s => {
        setWaStatus(s);
        if (s.configured && s.phoneNumberId) {
          setWaForm(f => ({ ...f, phoneNumberId: s.phoneNumberId ?? '', wabaId: s.wabaId ?? '' }));
        }
      })
      .catch(() => {})
      .finally(() => setWaLoading(false));
  }, [tenantId]);

  useEffect(() => { fetchWaStatus(); }, [fetchWaStatus]);

  const handleWaSave = async () => {
    if (!tenantId || !waForm.phoneNumberId || !waForm.wabaId || !waForm.accessToken) return;
    setWaSaving(true);
    setWaTestResult(null);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/whatsapp`, {
        method: 'PUT',
        body: JSON.stringify(waForm),
      });
      fetchWaStatus();
    } catch { /* ignore */ } finally { setWaSaving(false); }
  };

  const handleWaTest = async () => {
    if (!tenantId) return;
    setWaTestResult(null);
    try {
      const r = await apiFetch<{ connected: boolean; message: string }>(`/tenants/${tenantId}/notification-channels/whatsapp/test`, { method: 'POST' });
      setWaTestResult(r);
    } catch { setWaTestResult({ connected: false, message: 'Test request failed.' }); }
  };

  const handleWaActivate = async () => {
    if (!tenantId) return;
    setWaSaving(true);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/whatsapp/activate`, { method: 'POST' });
      fetchWaStatus();
    } catch { /* ignore */ } finally { setWaSaving(false); }
  };

  const handleWaDeactivate = async () => {
    if (!tenantId) return;
    setWaSaving(true);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/whatsapp/deactivate`, { method: 'POST' });
      fetchWaStatus();
    } catch { /* ignore */ } finally { setWaSaving(false); }
  };

  // ── Teams channel ──────────────────────────────────────────────────────
  const fetchTeamsStatus = useCallback(() => {
    if (!tenantId) return;
    setTeamsLoading(true);
    apiFetch<WebhookChannelStatus>(`/tenants/${tenantId}/notification-channels/teams`)
      .then(s => setTeamsStatus(s))
      .catch(() => {})
      .finally(() => setTeamsLoading(false));
  }, [tenantId]);

  useEffect(() => { fetchTeamsStatus(); }, [fetchTeamsStatus]);

  const handleTeamsSave = async () => {
    if (!tenantId || !teamsWebhookUrl) return;
    setTeamsSaving(true);
    setTeamsTestResult(null);
    setTeamsError(null);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/teams`, {
        method: 'PUT',
        body: JSON.stringify({ webhookUrl: teamsWebhookUrl }),
      });
      setTeamsWebhookUrl('');
      fetchTeamsStatus();
    } catch (err: any) { setTeamsError(err?.message ?? 'Failed to save Teams webhook.'); } finally { setTeamsSaving(false); }
  };

  const handleTeamsTest = async () => {
    if (!tenantId) return;
    setTeamsTestResult(null);
    setTeamsError(null);
    try {
      const r = await apiFetch<{ connected: boolean; message: string }>(`/tenants/${tenantId}/notification-channels/teams/test`, { method: 'POST' });
      setTeamsTestResult(r);
      if (r.connected) fetchTeamsStatus();
    } catch { setTeamsTestResult({ connected: false, message: 'Test request failed.' }); }
  };

  const handleTeamsEnable = async () => {
    if (!tenantId) return;
    setTeamsSaving(true);
    setTeamsError(null);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/teams/enable`, { method: 'POST' });
      fetchTeamsStatus();
    } catch (err: any) { setTeamsError(err?.message ?? 'Failed to enable Teams.'); } finally { setTeamsSaving(false); }
  };

  const handleTeamsDisable = async () => {
    if (!tenantId) return;
    setTeamsSaving(true);
    setTeamsError(null);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/teams/disable`, { method: 'POST' });
      fetchTeamsStatus();
    } catch (err: any) { setTeamsError(err?.message ?? 'Failed to disable Teams.'); } finally { setTeamsSaving(false); }
  };

  // ── Slack channel ──────────────────────────────────────────────────────
  const fetchSlackStatus = useCallback(() => {
    if (!tenantId) return;
    setSlackLoading(true);
    apiFetch<WebhookChannelStatus>(`/tenants/${tenantId}/notification-channels/slack`)
      .then(s => setSlackStatus(s))
      .catch(() => {})
      .finally(() => setSlackLoading(false));
  }, [tenantId]);

  useEffect(() => { fetchSlackStatus(); }, [fetchSlackStatus]);

  const handleSlackSave = async () => {
    if (!tenantId || !slackWebhookUrl) return;
    setSlackSaving(true);
    setSlackTestResult(null);
    setSlackError(null);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/slack`, {
        method: 'PUT',
        body: JSON.stringify({ webhookUrl: slackWebhookUrl }),
      });
      setSlackWebhookUrl('');
      fetchSlackStatus();
    } catch (err: any) { setSlackError(err?.message ?? 'Failed to save Slack webhook.'); } finally { setSlackSaving(false); }
  };

  const handleSlackTest = async () => {
    if (!tenantId) return;
    setSlackTestResult(null);
    setSlackError(null);
    try {
      const r = await apiFetch<{ connected: boolean; message: string }>(`/tenants/${tenantId}/notification-channels/slack/test`, { method: 'POST' });
      setSlackTestResult(r);
      if (r.connected) fetchSlackStatus();
    } catch { setSlackTestResult({ connected: false, message: 'Test request failed.' }); }
  };

  const handleSlackEnable = async () => {
    if (!tenantId) return;
    setSlackSaving(true);
    setSlackError(null);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/slack/enable`, { method: 'POST' });
      fetchSlackStatus();
    } catch (err: any) { setSlackError(err?.message ?? 'Failed to enable Slack.'); } finally { setSlackSaving(false); }
  };

  const handleSlackDisable = async () => {
    if (!tenantId) return;
    setSlackSaving(true);
    setSlackError(null);
    try {
      await apiFetch(`/tenants/${tenantId}/notification-channels/slack/disable`, { method: 'POST' });
      fetchSlackStatus();
    } catch (err: any) { setSlackError(err?.message ?? 'Failed to disable Slack.'); } finally { setSlackSaving(false); }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    setSeedResult(null);
    setSeedError(null);
    try {
      const result = await apiFetch<{ templates: number; sections: number; items: number }>(
        '/admin/seed-phase-gate-templates',
        { method: 'POST' },
      );
      setSeedResult(result);
      // Refresh templates list
      const updated = await apiFetch<ChecklistTemplate[]>('/checklist-templates');
      setTemplates(updated);
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      setSeedError(
        status === 403
          ? 'Seeding requires a platform super admin session (403).'
          : 'Failed to seed default templates.',
      );
    } finally {
      setSeeding(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // Group templates by phaseId
  const grouped = templates.reduce<Record<string, ChecklistTemplate[]>>((acc, t) => {
    const key = t.phaseId ?? 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  const phaseOrder = PHASE_ORDER;

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-sm)',
    padding: 24,
    marginBottom: 16,
  };

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
      <Sidebar />
      <main className="flex-1 px-8 py-6">
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 24 }}>Settings</h1>
        <div style={{ maxWidth: 640 }}>

          {/* Plan summary — issue #780. Visible to every tier so non-partner
              users finally have a place to see what plan they're on, what it
              costs (for STARTER/GROWTH), and an upgrade CTA when applicable. */}
          <PlanCard tenant={myTenant ?? null} />

          {/* CFGIA.A4 (#1539) — always-on "Re-run setup" action card. Admin-only
              (the onboarding wizard 403s non-admins). This is an action card, NOT
              a CFG-IA taxonomy section, so it sits above the taxonomy cluster and
              never touches the section anchors A1 establishes. The CTA resolves
              the tenant's OWN tier-specific onboarding flow (SME / individual /
              partner / full org wizard) via getOnboardingPathForTier, so an
              established tenant resumes exactly where a fresh one starts —
              satisfying "permanently reachable, no terminal done flag". No
              Sidebar entry: it would collide with the existing completion-gated
              "Setup" (ring-flow) nav item. */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-setup">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Organisation Setup</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Re-run the guided setup wizard at any time to review or update your infrastructure, deployment, QA, and agent configuration.
              </p>
              <Link
                to={getOnboardingPathForTier(tier)}
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'white',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Re-run setup
              </Link>
            </div>
          )}

          {/* Appearance */}
          <div style={cardStyle} data-testid="panel-appearance">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Appearance</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Choose your preferred colour theme.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              {themeOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPreference(opt.value)}
                  style={{
                    padding: '7px 18px',
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    fontWeight: preference === opt.value ? 600 : 500,
                    borderRadius: 'var(--radius-sm)',
                    border: preference === opt.value ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                    background: preference === opt.value ? 'rgba(124,58,237,0.08)' : 'var(--surface)',
                    color: preference === opt.value ? 'var(--primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    transition: 'all 150ms',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preferences — DASH.C1 (#825). Personal landing-page choice; saves
              on change via the self-scoped PATCH /users/me/preferences. */}
          <div style={cardStyle} data-testid="panel-landing">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Preferences</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Choose where BluePrint takes you when you log in.
            </p>
            <label htmlFor="default-landing-page" style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 8 }}>
              Default landing page
            </label>
            <select
              id="default-landing-page"
              data-testid="default-landing-page-select"
              value={landingPref}
              disabled={landingSaving}
              onChange={async (e) => {
                const value = e.target.value;
                const prev = landingPref;
                setLandingPref(value);   // optimistic
                setLandingSaving(true);
                try {
                  await apiFetch('/users/me/preferences', {
                    method: 'PATCH',
                    body: JSON.stringify({ default_landing_page: value }),
                  });
                } catch {
                  setLandingPref(prev);  // revert on failure
                } finally {
                  setLandingSaving(false);
                }
              }}
              style={{
                padding: '8px 12px',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                border: '1.5px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--surface)',
                color: 'var(--text)',
                minWidth: 220,
                cursor: landingSaving ? 'not-allowed' : 'pointer',
              }}
            >
              {LANDING_PAGES.map(p => (
                <option key={p} value={p}>{LANDING_LABELS[p]}</option>
              ))}
            </select>
          </div>

          {/* Sprint ACAD ACT.A8 (#1709) — Settings → Onboarding → Restart.
              Q8 adjudication: restart resets the SERVER-SIDE progress
              pointer only (POST /api/onboarding/:flowType/restart) — it
              never re-triggers the mutating side effects a flow may have
              already caused (the admin wizard's cohort/team creation,
              invites sent). Only the flow matching the viewer's OWN
              tenant_members role is shown; a user with no matching
              onboarding flow (e.g. TENANT_MEMBER, CLIENT) sees nothing here. */}
          {(myTenant?.role === 'STUDENT' || myTenant?.role === 'FACULTY_SUPERVISOR' || isAdmin) && (
            <div style={cardStyle} data-testid="panel-tour">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Onboarding</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Replay your first-login tour any time.
              </p>
              <button
                data-testid="onboarding-restart-button"
                disabled={onboardingRestarting}
                onClick={async () => {
                  const flowType = myTenant?.role === 'STUDENT'
                    ? 'STUDENT'
                    : myTenant?.role === 'FACULTY_SUPERVISOR'
                      ? 'FACULTY_SUPERVISOR'
                      : 'TENANT_ADMIN';
                  setOnboardingRestarting(true);
                  try {
                    await apiFetch(`/api/onboarding/${flowType}/restart`, { method: 'POST' });
                    const destination = flowType === 'STUDENT'
                      ? '/onboarding/student'
                      : flowType === 'FACULTY_SUPERVISOR'
                        ? '/onboarding/faculty-supervisor'
                        : getOnboardingPathForTier(myTenant?.subscriptionTier);
                    window.location.href = destination;
                  } finally {
                    setOnboardingRestarting(false);
                  }
                }}
                style={{
                  padding: '8px 18px',
                  fontFamily: 'var(--font-body)',
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 'var(--radius-sm)',
                  border: '1.5px solid var(--border)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  cursor: onboardingRestarting ? 'not-allowed' : 'pointer',
                }}
              >
                {onboardingRestarting ? 'Restarting…' : 'Restart onboarding'}
              </button>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              CFG-IA cluster anchor (#1536 A1 establishes; A2/A3 append;
              B1 #1541 folds QA Policy + Agent Model Config in + adds
              Storage + GitHub cards; B1 removes the old inline
              GitHubConnectionCard and BYOI Setup card)

              No section registry exists — Settings is inline cards. The CFG-IA
              sprint groups configuration sections under the D1 taxonomy, IN THIS
              ORDER, each a <Link to="/settings/<id>"> whose visibility gates to
              its destination's READ authorization (D13): credential destinations
              admin-gated, member-read destinations member-visible.

                Cloud/Mode (Infrastructure) → Storage → GitHub →
                Deployment → Quality & QA → Agents → Agent Model Config → Estimation

              A2/A3/B1 appended in order. Keep the block contiguous.
              ════════════════════════════════════════════════════════════════ */}

          {/* Infrastructure — CFG-IA.A1 (#1536). TENANT_ADMIN only (the page
              itself fail-closed redirects non-admins). Storage + version control
              + cloud credentials; A1 ships the cloud-credential (BYOC/AWS)
              surface and the editable BYOC/MANAGED deployment-mode toggle. */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-cloud">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Infrastructure</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Choose your deployment mode and, for Bring Your Own Cloud, manage the AWS account BluePrint deploys into.
              </p>
              <Link
                to="/settings/infrastructure"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'white',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Manage Infrastructure →
              </Link>
            </div>
          )}

          {/* Storage — B1 (#1541). Provider selector (GOOGLE_DRIVE/SHAREPOINT).
              ADMIN-ONLY per the #1595 hybrid Member-Settings-Visibility decision:
              integration/operational config (Storage, GitHub) is hidden from
              members, unlike governance config (Deployment/QA/Agents/Agent-Model/
              Estimation below), which stays member-read-only. This surface embeds
              a real write path — Drive OAuth — that a read-only presentation
              cannot safely expose to non-admins (#1594); hiding the card is the
              structural fix, mirroring Infrastructure above. The page itself
              also fail-closed redirects non-admins (SettingsStoragePage.tsx). */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-storage">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Storage</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Choose the storage provider for project artefacts (Google Drive or SharePoint) and manage Drive authorisation.
              </p>
              <Link
                to="/settings/storage"
                style={{
                  display: 'inline-block', padding: '8px 16px',
                  fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
                  color: 'white', background: 'var(--primary)', border: 'none',
                  borderRadius: 'var(--radius-sm)', textDecoration: 'none', transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Configure Storage →
              </Link>
            </div>
          )}

          {/* GitHub — B1 (#1541). Relocates the former inline GitHubConnectionCard
              into the cluster as a proper destination. ADMIN-ONLY per the #1595
              hybrid Member-Settings-Visibility decision (integration/operational
              config is hidden from members; the write path was already fully
              admin-gated server-side — this is a visibility-only change). The
              page itself also fail-closed redirects non-admins
              (SettingsGitHubPage.tsx). */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-github">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>GitHub</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Connect your GitHub organisation and manage OAuth credentials or a personal access token.
              </p>
              <Link
                to="/settings/github"
                style={{
                  display: 'inline-block', padding: '8px 16px',
                  fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
                  color: 'white', background: 'var(--primary)', border: 'none',
                  borderRadius: 'var(--radius-sm)', textDecoration: 'none', transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Configure GitHub →
              </Link>
            </div>
          )}

          {/* Deployment — CFG-IA.A2 (#1537). Deployment environments, CI
              pipeline, branch strategy, and release gates. MEMBER-VISIBLE (not
              admin-gated): this is a member-read / admin-write surface, the same
              shape as the QA Policy and Agent Model Configuration cards below, so
              any member may open it. Write authorization is enforced on the page
              via canEdit=isAdmin (a non-admin who clicks through lands read-only);
              card visibility is NOT a write grant. Per the CFG-IA card-gating
              rule, an entry card matches its destination's READ authorization, not
              a blanket admin gate — contrast Infrastructure above, which IS
              admin-gated only because its destination exposes AWS credentials. */}
          <div style={cardStyle} data-testid="panel-deployment">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Deployment</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Configure deployment environments, the CI pipeline, branch strategy, and release gates.
            </p>
            <Link
              to="/settings/deployment"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                fontWeight: 600,
                color: 'white',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                textDecoration: 'none',
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
            >
              Manage Deployment →
            </Link>
          </div>
          {/* Quality & QA — QA Policy. B1 (#1541) folded this card in from its
              former standalone position (QA.A3 #950 shipped the page); it now
              occupies the Quality & QA slot the A1 anchor reserved. MEMBER-VISIBLE
              (member-read; the page enforces admin-WRITE via canEdit) per the D13
              card-gating rule — do NOT admin-gate. */}
          <div style={cardStyle} data-testid="panel-qa-policy">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>QA Policy</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Set the pass rate threshold, required test types, and release gates the QA Agent enforces across your organisation.
            </p>
            <Link
              to="/settings/qa-policy"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                fontWeight: 600,
                color: 'white',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                textDecoration: 'none',
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
            >
              Configure QA Policy →
            </Link>
          </div>
          {/* Agents — CFG-IA.A3 (#1538). Phase activation, per-agent
              system-prompt overrides, and organisation-minimum exit-checklist
              defaults (tenant_agent_configs). Per-agent model selection stays
              GOVERNED under Agent Model Configuration (D8). MEMBER-VISIBLE
              (member-read; the page enforces admin-WRITE via canEdit={isAdmin}),
              matching the QA Policy and Agent Model cards — CFG-IA entry cards
              gate to their destination's READ authorization, not a blanket admin
              gate (Infrastructure stays admin-gated only because its destination
              is credential management). The governed Agent Model Config card
              now follows in the Agents area (Infrastructure → Deployment →
              Quality & QA → Agents → Agent Model Config). */}
          <div style={cardStyle} data-testid="panel-agent-config">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Agents</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Choose which agents run for each phase and customise their system-prompt prefixes and organisation-minimum exit-checklist items.
            </p>
            <Link
              to="/settings/agents"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                fontWeight: 600,
                color: 'white',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                textDecoration: 'none',
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
            >
              Configure Agents →
            </Link>
          </div>
          {/* Agent Model Configuration — B1 (#1541) folded this card in from its
              former standalone position, into the Agents area adjacent to the
              Agents card. The GOVERNED per-agent model store (its own
              propose/approve flow at /settings/agent-models). MEMBER-VISIBLE
              (member-read; admin-write enforced on the page) per the D13
              card-gating rule. Terminal cluster position. */}
          <div style={cardStyle} data-testid="panel-models">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Agent Model Configuration</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Choose which LLM model each phase agent uses. Changes require approval from a tenant admin or platform admin before taking effect.
            </p>
            <Link
              to="/settings/agent-models"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                fontWeight: 600,
                color: 'white',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                textDecoration: 'none',
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
            >
              Configure Agent Models →
            </Link>
          </div>
          {/* Estimation — B1 (#1541) moves this card from SettingsOrganisationPage
              into the CFG-IA cluster at the D1 tail position (after Agent Model
              Config). Member-read / admin-write → MEMBER-VISIBLE per D13 card-
              gating rule (write protection lives on the page via canEdit). */}
          <div style={cardStyle} data-testid="panel-estimation">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Estimation</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Choose how effort estimates appear across your projects — Story Points, EU/RU, or both.
            </p>
            <Link
              to="/settings/estimation"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                fontWeight: 600,
                color: 'white',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                textDecoration: 'none',
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
            >
              Configure Estimation →
            </Link>
          </div>
          {/* ── end CFG-IA cluster anchor ──────────────────────────────────── */}

          {/* Billing — partner accounts only (gated on partner_accounts
              membership, not tier; see useMyTenant.isPartner). Migrated to
              isSectionVisible(...) per issue #779 so the rule lives in one place. */}
          {isSectionVisible('billing', tier, { isPartner }) && (
            <div style={cardStyle}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Billing</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Review your plan, seats, usage, and invoice history.
              </p>
              <Link
                to="/settings/billing"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'white',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Open Billing Portal →
              </Link>
            </div>
          )}

          {/* Organisation / Account — solo plans see "Account" wording
              since Organisation implies a multi-person org (issue #779).
              TENANT_ADMIN only — the page itself redirects non-admins on
              mount, so this just avoids the click-then-bounce (issue #1676). */}
          {isAdmin && (
            <div style={cardStyle}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>{orgLabel}</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                {orgLabel === 'Account'
                  ? 'Manage your account profile and integrations.'
                  : 'Manage your organisation profile, team members, and integrations.'}
              </p>
              <Link
                to="/settings/organisation"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'white',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Manage {orgLabel} →
              </Link>
            </div>
          )}

          {/* Custody Transfer — CT.A5 (#2535). TENANT_ADMIN only — the page
              itself also fail-closed redirects non-admins
              (CustodyTransferPage.tsx). */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-custody">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Custody Transfer</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Move a project&rsquo;s access from one team member to another — revokes the outgoing person&rsquo;s access and
                assigns the incoming person&rsquo;s roles in one step.
              </p>
              <Link
                to="/settings/custody-transfers"
                style={{
                  display: 'inline-block', padding: '8px 16px',
                  fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
                  color: 'white', background: 'var(--primary)', border: 'none',
                  borderRadius: 'var(--radius-sm)', textDecoration: 'none', transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Manage Custody Transfers →
              </Link>
            </div>
          )}

          {/* Client approvals — #3185 APPROVE.2 2c. Admin rank only (isAdmin
              admits legacy ADMIN); the page itself also redirects non-admins. */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-client-approvals">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Client approvals</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Choose which deliverables need your client&rsquo;s approval by default. Projects can override it.
              </p>
              <Link
                to="/settings/client-approvals"
                style={{
                  display: 'inline-block', padding: '8px 16px',
                  fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
                  color: 'white', background: 'var(--primary)', border: 'none',
                  borderRadius: 'var(--radius-sm)', textDecoration: 'none', transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Manage client approvals →
              </Link>
            </div>
          )}

          {/* Branding — BRAND.C2 (#840). TENANT_ADMIN only (the page itself
              redirects non-admins). Display name + brand colours BluePrint
              applies to generated artefacts. */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-branding">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Branding</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Set the display name and brand colours applied to your generated artefacts.
              </p>
              <Link
                to="/settings/branding"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'white',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Manage Branding →
              </Link>
            </div>
          )}

          {/* Certificate / Institution Settings — #1803 (P1, gating).
              TENANT_ADMIN only, ACADEMIC tier only (academic_tenants has no
              row for other tiers, so the page's GET would just 404 for
              them). Institution name + signatory shown on issued
              certificates — every field frozen at issuance. */}
          {isAdmin && tier === 'ACADEMIC' && (
            <div style={cardStyle} data-testid="panel-certificate">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Certificate / Institution Settings</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Set your institution&apos;s name and signatory as they appear on issued certificates.
              </p>
              <Link
                to="/settings/certificate-institution"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'white',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                Manage Certificate Settings →
              </Link>
            </div>
          )}

          {/* CFG.PA.A2 — Platform Access Log (TENANT_ADMIN only). Also
              hidden for INDIVIDUAL via isSectionVisible (#779) — solo
              tenants don't have admin/non-admin distinction worth surfacing. */}
          {isAdmin && isSectionVisible('platform-access-log', tier) && (
            <div style={cardStyle} data-testid="panel-access-log">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Security — Platform Access Log</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Review every time BluePrint support staff has been granted access to your workspace.
                The log is append-only and cannot be edited or deleted.
              </p>
              <Link
                to="/settings/security/platform-access-log"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'white',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                View Access Log →
              </Link>
            </div>
          )}

          {/* PKGV.D4 (#804) — Knowledge Graph Health. TENANT_ADMIN only,
              same pattern as Infrastructure/Branding/Platform Access Log
              above: a link-out card, not inline charts (Sprint-D4-
              adjudication.md Q1). Gated on EXEC_PKG_SUMMARY_ENABLED
              (a plain static flag, same kill-switch pattern as
              PKG_NEIGHBOURHOOD_PANEL_ENABLED) in addition to isAdmin —
              two independent gates, not one role-aware flag. */}
          {isAdmin && isFeatureEnabled('EXEC_PKG_SUMMARY_ENABLED') && (
            <div style={cardStyle} data-testid="panel-knowledge-graph">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Knowledge Graph Health</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                See your organisation&apos;s knowledge graph health across all projects — edge density, governance gaps, and enrichment usage.
              </p>
              <Link
                to="/settings/pkg-health"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'white',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  textDecoration: 'none',
                  transition: 'box-shadow 150ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-primary)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'; }}
              >
                View Knowledge Graph Health →
              </Link>
            </div>
          )}

          {/* Notification Preferences */}
          <div style={cardStyle} data-testid="panel-email">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Notification Preferences</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Choose how and when you receive notifications.
            </p>
            {notifPrefs.filter(p => p.channel === 'EMAIL').map(pref => (
              <div key={pref.channel} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Email Notifications</span>
                  <button
                    onClick={async () => {
                      setNotifSaving(true);
                      try {
                        await apiFetch('/notifications/preferences', {
                          method: 'PATCH',
                          body: JSON.stringify({ channel: 'EMAIL', frequency: pref.frequency, enabled: !pref.enabled }),
                        });
                        setNotifPrefs(prev => prev.map(p => p.channel === 'EMAIL' ? { ...p, enabled: !p.enabled } : p));
                      } catch { /* ignore */ } finally { setNotifSaving(false); }
                    }}
                    disabled={notifSaving}
                    style={{
                      padding: '3px 10px',
                      fontFamily: 'var(--font-body)',
                      fontSize: 11,
                      fontWeight: 600,
                      borderRadius: 'var(--radius-sm)',
                      border: `1.5px solid ${pref.enabled ? '#10b981' : 'var(--border)'}`,
                      background: pref.enabled ? 'rgba(16,185,129,0.08)' : 'var(--surface)',
                      color: pref.enabled ? '#10b981' : 'var(--text-secondary)',
                      cursor: 'pointer',
                    }}
                  >
                    {pref.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {frequencyOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={async () => {
                        setNotifSaving(true);
                        try {
                          await apiFetch('/notifications/preferences', {
                            method: 'PATCH',
                            body: JSON.stringify({ channel: 'EMAIL', frequency: opt.value, enabled: pref.enabled }),
                          });
                          setNotifPrefs(prev => prev.map(p => p.channel === 'EMAIL' ? { ...p, frequency: opt.value } : p));
                        } catch { /* ignore */ } finally { setNotifSaving(false); }
                      }}
                      disabled={notifSaving}
                      title={opt.description}
                      style={{
                        padding: '7px 16px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        fontWeight: pref.frequency === opt.value ? 600 : 500,
                        borderRadius: 'var(--radius-sm)',
                        border: pref.frequency === opt.value ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                        background: pref.frequency === opt.value ? 'rgba(124,58,237,0.08)' : 'var(--surface)',
                        color: pref.frequency === opt.value ? 'var(--primary)' : 'var(--text-secondary)',
                        cursor: 'pointer',
                        transition: 'all 150ms',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {(pref.frequency === 'DAILY') && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginRight: 8 }}>
                      Digest time (UTC):
                    </label>
                    <input
                      type="time"
                      defaultValue={pref.digestTime ?? '09:00'}
                      onChange={async (e) => {
                        const time = e.target.value;
                        if (!time) return;
                        try {
                          await apiFetch('/notifications/preferences', {
                            method: 'PATCH',
                            body: JSON.stringify({ channel: 'EMAIL', frequency: 'DAILY', digestTime: time, enabled: pref.enabled }),
                          });
                          setNotifPrefs(prev => prev.map(p => p.channel === 'EMAIL' ? { ...p, digestTime: time } : p));
                        } catch { /* ignore */ }
                      }}
                      style={{
                        padding: '4px 8px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    />
                  </div>
                )}
              </div>
            ))}

            {/* Agent-pause desktop notification (WEB_PUSH) — debounce preference.
                Always rendered so the user can adjust/disable here regardless of
                whether they hit the PushPermissionBanner flow. The toggle below
                flips the enabled column; the 30s/60s picker writes
                pushDebounceSeconds. */}
            {(() => {
              const webPush = notifPrefs.find(p => p.channel === 'WEB_PUSH');
              const webPushEnabled = webPush?.enabled ?? true;
              const currentDebounce = (webPush?.pushDebounceSeconds ?? 30) as 30 | 60;
              return (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      Agent pause desktop alerts
                    </span>
                    <button
                      data-testid="web-push-toggle"
                      onClick={async () => {
                        setNotifSaving(true);
                        try {
                          await apiFetch('/notifications/preferences', {
                            method: 'PATCH',
                            body: JSON.stringify({
                              channel: 'WEB_PUSH',
                              enabled: !webPushEnabled,
                              pushDebounceSeconds: currentDebounce,
                            }),
                          });
                          setNotifPrefs(prev => {
                            const next = prev.filter(p => p.channel !== 'WEB_PUSH');
                            next.push({
                              id: webPush?.id ?? null,
                              channel: 'WEB_PUSH',
                              frequency: 'REAL_TIME',
                              digestTime: null,
                              enabled: !webPushEnabled,
                              pushDebounceSeconds: currentDebounce,
                            });
                            return next;
                          });
                        } catch { /* ignore */ } finally { setNotifSaving(false); }
                      }}
                      disabled={notifSaving}
                      style={{
                        padding: '3px 10px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: 'var(--radius-sm)',
                        border: `1.5px solid ${webPushEnabled ? '#10b981' : 'var(--border)'}`,
                        background: webPushEnabled ? 'rgba(16,185,129,0.08)' : 'var(--surface)',
                        color: webPushEnabled ? '#10b981' : 'var(--text-secondary)',
                        cursor: 'pointer',
                      }}
                    >
                      {webPushEnabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    When an agent pauses for your input, wait this long before sending a desktop notification so you don&apos;t get pinged for replies you&apos;re already typing.
                  </p>
                  <div style={{ display: 'flex', gap: 8 }} role="radiogroup" aria-label="Agent pause debounce">
                    {[30, 60].map(sec => {
                      const active = currentDebounce === sec;
                      return (
                        <button
                          key={sec}
                          data-testid={`web-push-debounce-${sec}`}
                          role="radio"
                          aria-checked={active}
                          disabled={notifSaving || !webPushEnabled}
                          onClick={async () => {
                            setNotifSaving(true);
                            try {
                              await apiFetch('/notifications/preferences', {
                                method: 'PATCH',
                                body: JSON.stringify({
                                  channel: 'WEB_PUSH',
                                  enabled: webPushEnabled,
                                  pushDebounceSeconds: sec,
                                }),
                              });
                              setNotifPrefs(prev => {
                                const next = prev.filter(p => p.channel !== 'WEB_PUSH');
                                next.push({
                                  id: webPush?.id ?? null,
                                  channel: 'WEB_PUSH',
                                  frequency: 'REAL_TIME',
                                  digestTime: null,
                                  enabled: webPushEnabled,
                                  pushDebounceSeconds: sec,
                                });
                                return next;
                              });
                            } catch { /* ignore */ } finally { setNotifSaving(false); }
                          }}
                          style={{
                            padding: '7px 16px',
                            fontFamily: 'var(--font-body)',
                            fontSize: 13,
                            fontWeight: active ? 600 : 500,
                            borderRadius: 'var(--radius-sm)',
                            border: active ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                            background: active ? 'rgba(124,58,237,0.08)' : 'var(--surface)',
                            color: active ? 'var(--primary)' : 'var(--text-secondary)',
                            cursor: (notifSaving || !webPushEnabled) ? 'not-allowed' : 'pointer',
                            opacity: webPushEnabled ? 1 : 0.5,
                          }}
                        >
                          {sec}s
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Phone number for WhatsApp delivery */}
            {phoneLoaded && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 8 }}>
                  WhatsApp Phone Number
                </span>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                  Required for WhatsApp notifications. Must be E.164 format (e.g. +1234567890).
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="tel"
                    placeholder="+1234567890"
                    value={userPhone}
                    onChange={e => setUserPhone(e.target.value)}
                    style={{
                      padding: '7px 12px',
                      fontFamily: 'var(--font-body)',
                      fontSize: 13,
                      border: '1.5px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--surface)',
                      color: 'var(--text)',
                      width: 200,
                    }}
                  />
                  <button
                    onClick={async () => {
                      if (!userPhone || !/^\+[1-9]\d{6,14}$/.test(userPhone)) return;
                      setPhoneSaving(true);
                      try {
                        await apiFetch('/notifications/preferences/phone', {
                          method: 'PATCH',
                          body: JSON.stringify({ phone: userPhone }),
                        });
                      } catch { /* ignore */ } finally { setPhoneSaving(false); }
                    }}
                    disabled={phoneSaving || !userPhone || !/^\+[1-9]\d{6,14}$/.test(userPhone)}
                    style={{
                      padding: '7px 14px',
                      fontFamily: 'var(--font-body)',
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      background: 'var(--primary)',
                      color: 'white',
                      cursor: (phoneSaving || !userPhone) ? 'default' : 'pointer',
                      opacity: (phoneSaving || !userPhone || !/^\+[1-9]\d{6,14}$/.test(userPhone)) ? 0.6 : 1,
                    }}
                  >
                    {phoneSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp Channel */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-whatsapp">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>WhatsApp Notifications</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Configure WhatsApp Business API for real-time notifications. Email fallback remains active until Meta approves your templates.
              </p>

              {waLoading ? (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading...</p>
              ) : waStatus?.templatesApproved ? (
                /* ── Active state ─────────────────────────────────────── */
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{
                      display: 'inline-block',
                      width: 8, height: 8,
                      borderRadius: '50%',
                      background: '#10b981',
                    }} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#10b981' }}>
                      WhatsApp Active
                    </span>
                  </div>
                  {waStatus.phoneNumberId && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                      Phone Number ID: {waStatus.phoneNumberId.slice(0, 4)}{'****'}{waStatus.phoneNumberId.slice(-4)}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => void handleWaTest()}
                      style={{
                        padding: '7px 16px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: 'var(--radius-sm)',
                        border: '1.5px solid var(--border)',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                        cursor: 'pointer',
                      }}
                    >
                      Send Test Message
                    </button>
                    <button
                      onClick={() => void handleWaDeactivate()}
                      disabled={waSaving}
                      style={{
                        padding: '7px 16px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: 'var(--radius-sm)',
                        border: '1.5px solid #ef4444',
                        background: 'rgba(239,68,68,0.08)',
                        color: '#ef4444',
                        cursor: waSaving ? 'default' : 'pointer',
                        opacity: waSaving ? 0.6 : 1,
                      }}
                    >
                      Deactivate
                    </button>
                  </div>
                  {waTestResult && (
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 12,
                      marginTop: 8,
                      color: waTestResult.connected ? '#10b981' : '#ef4444',
                    }}>
                      {waTestResult.message}
                    </p>
                  )}
                </div>
              ) : (
                /* ── Pending / unconfigured state ─────────────────────── */
                <div>
                  {waStatus?.configured && (
                    <div style={{
                      background: 'rgba(245,158,11,0.08)',
                      border: '1px solid rgba(245,158,11,0.3)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '10px 14px',
                      marginBottom: 16,
                    }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#b45309' }}>
                        WhatsApp configured. Awaiting Meta template approval — email fallback is active.
                      </p>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                    <input
                      type="text"
                      placeholder="Phone Number ID"
                      value={waForm.phoneNumberId}
                      onChange={e => setWaForm(f => ({ ...f, phoneNumberId: e.target.value }))}
                      style={{
                        padding: '8px 12px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="WABA ID"
                      value={waForm.wabaId}
                      onChange={e => setWaForm(f => ({ ...f, wabaId: e.target.value }))}
                      style={{
                        padding: '8px 12px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    />
                    <PasswordInput
                      placeholder="Access Token"
                      value={waForm.accessToken}
                      onChange={e => setWaForm(f => ({ ...f, accessToken: e.target.value }))}
                      style={{
                        padding: '8px 12px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        border: '1.5px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => void handleWaSave()}
                      disabled={waSaving || !waForm.phoneNumberId || !waForm.wabaId || !waForm.accessToken}
                      style={{
                        padding: '7px 16px',
                        fontFamily: 'var(--font-body)',
                        fontSize: 13,
                        fontWeight: 600,
                        borderRadius: 'var(--radius-sm)',
                        border: 'none',
                        background: 'var(--primary)',
                        color: 'white',
                        cursor: (waSaving || !waForm.phoneNumberId || !waForm.wabaId || !waForm.accessToken) ? 'default' : 'pointer',
                        opacity: (waSaving || !waForm.phoneNumberId || !waForm.wabaId || !waForm.accessToken) ? 0.6 : 1,
                      }}
                    >
                      {waSaving ? 'Saving...' : 'Save Configuration'}
                    </button>
                    {waStatus?.configured && (
                      <>
                        <button
                          onClick={() => void handleWaTest()}
                          style={{
                            padding: '7px 16px',
                            fontFamily: 'var(--font-body)',
                            fontSize: 13,
                            fontWeight: 600,
                            borderRadius: 'var(--radius-sm)',
                            border: '1.5px solid var(--border)',
                            background: 'var(--surface)',
                            color: 'var(--text)',
                            cursor: 'pointer',
                          }}
                        >
                          Test Connection
                        </button>
                        <button
                          onClick={() => void handleWaActivate()}
                          disabled={waSaving}
                          style={{
                            padding: '7px 16px',
                            fontFamily: 'var(--font-body)',
                            fontSize: 13,
                            fontWeight: 600,
                            borderRadius: 'var(--radius-sm)',
                            border: '1.5px solid #10b981',
                            background: 'rgba(16,185,129,0.08)',
                            color: '#10b981',
                            cursor: waSaving ? 'default' : 'pointer',
                            opacity: waSaving ? 0.6 : 1,
                          }}
                        >
                          Activate
                        </button>
                      </>
                    )}
                  </div>
                  {waTestResult && (
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 12,
                      marginTop: 8,
                      color: waTestResult.connected ? '#10b981' : '#ef4444',
                    }}>
                      {waTestResult.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Microsoft Teams Channel — hidden for solo plans (no MS Teams
              workspace without an org), per issue #779. */}
          {isAdmin && isSectionVisible('ms-teams-channel', tier) && (
            <div style={cardStyle} data-testid="panel-teams">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Microsoft Teams Notifications</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Send notifications via Teams incoming webhook. Get your webhook URL from Teams Channel &gt; Connectors &gt; Incoming Webhook.
              </p>

              {teamsLoading ? (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading...</p>
              ) : teamsStatus?.enabled ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#10b981' }}>Teams Active</span>
                  </div>
                  {teamsStatus.webhookUrl && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                      Webhook: {teamsStatus.webhookUrl}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => void handleTeamsTest()} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' }}>
                      Send Test Card
                    </button>
                    <button onClick={() => void handleTeamsDisable()} disabled={teamsSaving} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: '1.5px solid #ef4444', background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: teamsSaving ? 'default' : 'pointer', opacity: teamsSaving ? 0.6 : 1 }}>
                      Disable
                    </button>
                  </div>
                  {teamsTestResult && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, marginTop: 8, color: teamsTestResult.connected ? '#10b981' : '#ef4444' }}>
                      {teamsTestResult.message}
                    </p>
                  )}
                  {teamsError && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, marginTop: 8, color: '#ef4444' }}>{teamsError}</p>
                  )}
                </div>
              ) : (
                <div>
                  {teamsStatus?.configured && (
                    <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16 }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#1d4ed8' }}>
                        Teams webhook configured. Test connection and enable to start receiving notifications.
                      </p>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                    <PasswordInput
                      placeholder="Teams Incoming Webhook URL"
                      value={teamsWebhookUrl}
                      onChange={e => setTeamsWebhookUrl(e.target.value)}
                      style={{ padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => void handleTeamsSave()} disabled={teamsSaving || !teamsWebhookUrl} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--primary)', color: 'white', cursor: (teamsSaving || !teamsWebhookUrl) ? 'default' : 'pointer', opacity: (teamsSaving || !teamsWebhookUrl) ? 0.6 : 1 }}>
                      {teamsSaving ? 'Saving...' : 'Save Webhook'}
                    </button>
                    {teamsStatus?.configured && (
                      <>
                        <button onClick={() => void handleTeamsTest()} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' }}>
                          Test Connection
                        </button>
                        <button onClick={() => void handleTeamsEnable()} disabled={teamsSaving} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: '1.5px solid #10b981', background: 'rgba(16,185,129,0.08)', color: '#10b981', cursor: teamsSaving ? 'default' : 'pointer', opacity: teamsSaving ? 0.6 : 1 }}>
                          Enable
                        </button>
                      </>
                    )}
                  </div>
                  {teamsTestResult && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, marginTop: 8, color: teamsTestResult.connected ? '#10b981' : '#ef4444' }}>
                      {teamsTestResult.message}
                    </p>
                  )}
                  {teamsError && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, marginTop: 8, color: '#ef4444' }}>{teamsError}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Slack Channel */}
          {isAdmin && (
            <div style={cardStyle} data-testid="panel-slack">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Slack Notifications</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Send notifications via Slack incoming webhook. Get your webhook URL from Slack &gt; Apps &gt; Incoming Webhooks &gt; Add to Slack.
              </p>

              {slackLoading ? (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading...</p>
              ) : slackStatus?.enabled ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#10b981' }}>Slack Active</span>
                  </div>
                  {slackStatus.webhookUrl && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                      Webhook: {slackStatus.webhookUrl}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => void handleSlackTest()} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' }}>
                      Send Test Message
                    </button>
                    <button onClick={() => void handleSlackDisable()} disabled={slackSaving} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: '1.5px solid #ef4444', background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: slackSaving ? 'default' : 'pointer', opacity: slackSaving ? 0.6 : 1 }}>
                      Disable
                    </button>
                  </div>
                  {slackTestResult && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, marginTop: 8, color: slackTestResult.connected ? '#10b981' : '#ef4444' }}>
                      {slackTestResult.message}
                    </p>
                  )}
                  {slackError && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, marginTop: 8, color: '#ef4444' }}>{slackError}</p>
                  )}
                </div>
              ) : (
                <div>
                  {slackStatus?.configured && (
                    <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16 }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#1d4ed8' }}>
                        Slack webhook configured. Test connection and enable to start receiving notifications.
                      </p>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                    <PasswordInput
                      placeholder="Slack Incoming Webhook URL"
                      value={slackWebhookUrl}
                      onChange={e => setSlackWebhookUrl(e.target.value)}
                      style={{ padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => void handleSlackSave()} disabled={slackSaving || !slackWebhookUrl} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--primary)', color: 'white', cursor: (slackSaving || !slackWebhookUrl) ? 'default' : 'pointer', opacity: (slackSaving || !slackWebhookUrl) ? 0.6 : 1 }}>
                      {slackSaving ? 'Saving...' : 'Save Webhook'}
                    </button>
                    {slackStatus?.configured && (
                      <>
                        <button onClick={() => void handleSlackTest()} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' }}>
                          Test Connection
                        </button>
                        <button onClick={() => void handleSlackEnable()} disabled={slackSaving} style={{ padding: '7px 16px', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, borderRadius: 'var(--radius-sm)', border: '1.5px solid #10b981', background: 'rgba(16,185,129,0.08)', color: '#10b981', cursor: slackSaving ? 'default' : 'pointer', opacity: slackSaving ? 0.6 : 1 }}>
                          Enable
                        </button>
                      </>
                    )}
                  </div>
                  {slackTestResult && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, marginTop: 8, color: slackTestResult.connected ? '#10b981' : '#ef4444' }}>
                      {slackTestResult.message}
                    </p>
                  )}
                  {slackError && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, marginTop: 8, color: '#ef4444' }}>{slackError}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tool Integrations (Jira / ADO / Confluence / Notion) —
              hidden for solo plans, per issue #779. */}
          {isAdmin && isSectionVisible('tool-integrations', tier) && (
            <div style={cardStyle} data-testid="panel-tool-integrations">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                Tool Integrations
              </h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Connect project management and documentation tools to import stories, sprints, and artefacts into BluePrint.
              </p>

              {toolsLoading ? (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
              ) : (
                <>
                  {/* Connected tools */}
                  {toolConnections.filter(c => c.status === 'connected').length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      {toolConnections.filter(c => c.status === 'connected').map(conn => (
                        <div
                          key={conn.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)',
                            marginBottom: 8,
                          }}
                        >
                          <div>
                            <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                              {conn.displayName || conn.toolType}
                            </span>
                            <span style={{
                              display: 'inline-block',
                              marginLeft: 8,
                              padding: '2px 8px',
                              fontSize: 11,
                              fontWeight: 600,
                              borderRadius: 'var(--radius-sm)',
                              background: '#10b981',
                              color: 'white',
                            }}>
                              Connected
                            </span>
                            {conn.lastSyncAt && (
                              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginLeft: 12 }}>
                                Last sync: {new Date(conn.lastSyncAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              apiFetch(`/tools/connections/${conn.id}`, {
                                method: 'DELETE',
                              })
                                .then(() => setToolConnections(prev => prev.filter(c => c.id !== conn.id)))
                                .catch(() => {});
                            }}
                            style={{
                              padding: '4px 12px',
                              fontFamily: 'var(--font-display)',
                              fontSize: 12,
                              fontWeight: 600,
                              color: 'var(--error)',
                              background: 'transparent',
                              border: '1px solid var(--error)',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                            }}
                          >
                            Disconnect
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Connect buttons */}
                  <div style={{ display: 'flex', gap: 12 }}>
                    {!toolConnections.some(c => c.toolType === 'JIRA' && c.status === 'connected') && (
                      <button
                        onClick={() => {
                          apiFetch<{ authUrl: string }>('/tools/jira/connect')
                            .then(r => { window.location.href = r.authUrl; })
                            .catch(() => {});
                        }}
                        style={{
                          padding: '8px 16px',
                          fontFamily: 'var(--font-display)',
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'white',
                          background: '#0052CC',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                        }}
                      >
                        Connect Jira
                      </button>
                    )}
                    {!toolConnections.some(c => c.toolType === 'ADO' && c.status === 'connected') && (
                      <button
                        onClick={() => {
                          apiFetch<{ authUrl: string }>('/tools/ado/connect')
                            .then(r => { window.location.href = r.authUrl; })
                            .catch(() => {});
                        }}
                        style={{
                          padding: '8px 16px',
                          fontFamily: 'var(--font-display)',
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'white',
                          background: '#0078D4',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                        }}
                      >
                        Connect Azure DevOps
                      </button>
                    )}
                    {!toolConnections.some(c => c.toolType === 'CONFLUENCE' && c.status === 'connected') && (
                      <button
                        onClick={() => {
                          apiFetch<{ authUrl: string }>('/tools/confluence/connect')
                            .then(r => { window.location.href = r.authUrl; })
                            .catch(() => {});
                        }}
                        style={{
                          padding: '8px 16px',
                          fontFamily: 'var(--font-display)',
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'white',
                          background: '#172B4D',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                        }}
                      >
                        Connect Confluence
                      </button>
                    )}
                    {!toolConnections.some(c => c.toolType === 'NOTION' && c.status === 'connected') && (
                      <button
                        onClick={() => {
                          apiFetch<{ authUrl: string }>('/tools/notion/connect')
                            .then(r => { window.location.href = r.authUrl; })
                            .catch(() => {});
                        }}
                        style={{
                          padding: '8px 16px',
                          fontFamily: 'var(--font-display)',
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'white',
                          background: '#000000',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                        }}
                      >
                        Connect Notion
                      </button>
                    )}
                  </div>

                  {/* #3711 — Figma token + per-project allow-list. */}
                  <FigmaConnectionSection
                    connected={toolConnections.some(c => c.toolType === 'FIGMA' && c.status === 'connected')}
                    onConnected={() => {
                      apiFetch<ToolConnection[]>('/tools/connections').then(setToolConnections).catch(() => {});
                    }}
                  />
                </>
              )}
            </div>
          )}

          {/* Phase Gate Templates — hidden for solo plans (no team
              approvals to template), per issue #779. */}
          {isSectionVisible('phase-gate-templates', tier) && (
          <div style={cardStyle} data-testid="panel-gate-templates">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>Phase Gate Templates</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {seedResult && (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {seedResult.templates === 0
                      ? 'Already up to date'
                      : `Created ${seedResult.templates} templates, ${seedResult.sections} sections, ${seedResult.items} items`}
                  </p>
                )}
                {seedError && (
                  <p role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--danger, #c0392b)' }}>{seedError}</p>
                )}
                {isSuperAdmin && (
                <button
                  onClick={() => void handleSeedDefaults()}
                  disabled={seeding}
                  style={{
                    padding: '6px 14px',
                    fontFamily: 'var(--font-body)',
                    fontSize: 12,
                    fontWeight: 600,
                    borderRadius: 'var(--radius-sm)',
                    border: '1.5px solid var(--border)',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    cursor: seeding ? 'default' : 'pointer',
                    opacity: seeding ? 0.6 : 1,
                    transition: 'all 150ms',
                  }}
                >
                  {seeding ? 'Seeding…' : 'Seed Defaults'}
                </button>
                )}
              </div>
            </div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Default entry and exit checklists for each phase. Used by the Phase Gate Agent to validate transitions.
            </p>

            {templatesLoading ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
            ) : templates.length === 0 ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                {adminRoleLoading
                  ? 'No templates yet.'
                  : isSuperAdmin
                  ? 'No templates yet. Click "Seed Defaults" to create the standard phase gate templates.'
                  : 'No templates yet. A platform super admin can create the standard phase gate templates.'}
              </p>
            ) : (
              phaseOrder.filter(p => grouped[p]?.length).map(phaseKey => (
                <div key={phaseKey} style={{ marginBottom: 16 }}>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                    {PHASE_LABELS[phaseKey] ?? phaseKey}
                  </p>
                  {grouped[phaseKey].map(t => {
                    const badge = TYPE_BADGE[t.checklistType] ?? TYPE_BADGE['GENERAL'];
                    const isOpen = expandedIds.has(t.id);
                    return (
                      <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 6, overflow: 'hidden' }}>
                        <button
                          onClick={() => toggleExpand(t.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            width: '100%',
                            padding: '10px 14px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          <span style={{ fontSize: 11, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 150ms', color: 'var(--text-secondary)' }}>▶</span>
                          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', flex: 1 }}>{t.name}</span>
                          <span style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: 20,
                            background: `${badge.color}18`,
                            color: badge.color,
                            border: `1px solid ${badge.color}40`,
                          }}>
                            {badge.label}
                          </span>
                        </button>
                        {isOpen && (
                          <div style={{ padding: '0 14px 12px', borderTop: '1px solid var(--border)' }}>
                            {t.description && (
                              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 10, marginBottom: 8 }}>
                                {t.description}
                              </p>
                            )}
                            <TemplateItems templateId={t.id} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
          )}

          {/* Escalation Automation — hidden for solo plans (no team to
              escalate to), per issue #779. */}
          {isAdmin && isSectionVisible('escalation-automation', tier) && (
            <div style={cardStyle} data-testid="panel-escalation">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Escalation Automation</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Configure automatic escalation paths for overdue approvals and stalled phases. These are tenant-level defaults; projects can override.
              </p>
              {escalationLoading ? (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading...</p>
              ) : (
                <>
                  {escalationRules.map(rule => {
                    const isApproval = rule.ruleType === 'CLIENT_APPROVAL_OVERDUE';
                    const label = isApproval ? 'Client Approval Escalations' : 'Phase Stall Escalations';
                    type Tier = { field: string; dayField: 'stallNotifyDays' | 'stallUrgentDays' | 'stallPartnerDays' | 'approvalNotifyDays' | 'approvalUrgentDays' | 'approvalRenewalDays' | 'approvalEscalateDays'; label: string };
                    const toggles: Tier[] = isApproval
                      ? [
                          { field: 'day3Reminder',      dayField: 'approvalNotifyDays',   label: 'Send reminder to client' },
                          { field: 'day5Urgent',         dayField: 'approvalUrgentDays',   label: 'Send URGENT reminder to client' },
                          { field: 'day7Renewal',        dayField: 'approvalRenewalDays',  label: 'Renew magic link and resend' },
                          { field: 'day10PartnerAlert',  dayField: 'approvalEscalateDays', label: 'Alert partner / delivery manager' },
                        ]
                      : [
                          { field: 'day3Reminder',      dayField: 'stallNotifyDays',      label: 'Notify phase assignee' },
                          { field: 'day5Urgent',         dayField: 'stallUrgentDays',      label: 'Send URGENT to assignee + admin' },
                          { field: 'day14PartnerAlert',  dayField: 'stallPartnerDays',     label: 'Alert partner' },
                        ];

                    return (
                      <div key={rule.ruleType} style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                          <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
                          <button
                            onClick={() => void handleEscalationToggle(rule.ruleType, 'enabled', !rule.enabled)}
                            disabled={escalationSaving}
                            style={{
                              padding: '3px 10px',
                              fontFamily: 'var(--font-body)',
                              fontSize: 11,
                              fontWeight: 600,
                              borderRadius: 'var(--radius-sm)',
                              border: `1.5px solid ${rule.enabled ? '#10b981' : 'var(--border)'}`,
                              background: rule.enabled ? 'rgba(16,185,129,0.08)' : 'var(--surface)',
                              color: rule.enabled ? '#10b981' : 'var(--text-secondary)',
                              cursor: 'pointer',
                            }}
                          >
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </button>
                        </div>
                        {rule.enabled && (
                          <div style={{ paddingLeft: 16 }}>
                            {toggles.map(t => {
                              const day = effectiveDay(rule, t.dayField);
                              const placeholder = String(ESCALATION_PLATFORM_DEFAULTS[t.dayField]);
                              return (
                                <div key={t.field} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                  <input
                                    type="checkbox"
                                    checked={!!rule[t.field]}
                                    onChange={() => void handleEscalationToggle(rule.ruleType, t.field, !rule[t.field])}
                                    disabled={escalationSaving}
                                    style={{ accentColor: 'var(--primary)', cursor: 'pointer' }}
                                  />
                                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>Day</span>
                                  <input
                                    type="number"
                                    min={1}
                                    max={30}
                                    value={day ?? ''}
                                    placeholder={placeholder}
                                    onChange={e => handleDayChange(rule.ruleType, t.dayField, e.target.value)}
                                    disabled={escalationSaving || !rule[t.field]}
                                    data-testid={`escalation-day-${rule.ruleType}-${t.dayField}`}
                                    style={{
                                      width: 56, padding: '2px 6px', fontSize: 13,
                                      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                                      background: 'var(--bg)', color: 'var(--text)',
                                    }}
                                  />
                                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>: {t.label}</span>
                                </div>
                              );
                            })}
                            {/* #3188 — present only when the API sends the key (after 3b's switch-on). */}
                            {isApproval && 'allowProceedAtRisk' in rule && (
                              <ProceedAtRiskSetting
                                stored={typeof rule.allowProceedAtRisk === 'boolean' ? rule.allowProceedAtRisk : null}
                                effective={typeof escalationEffective?.allowProceedAtRisk === 'boolean' ? escalationEffective.allowProceedAtRisk : true}
                                disabled={escalationSaving}
                                onChange={v => void handleAllowProceedAtRisk(v)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {escalationRules.length > 0 && (
                    <>
                      {escalationSaveError && (
                        <div data-testid="escalation-save-error" style={{
                          padding: '8px 12px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
                          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 12,
                          marginBottom: 10,
                        }}>
                          {escalationSaveError}
                        </div>
                      )}
                      {escalationSavedAt && !escalationSaveError && (
                        <div data-testid="escalation-save-success" style={{
                          padding: '8px 12px', background: 'rgba(16,185,129,0.08)', color: '#10b981',
                          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 12,
                          marginBottom: 10,
                        }}>
                          Saved
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleEscalationSave()}
                        disabled={escalationSaving || Object.keys(escalationDayEdits).length === 0}
                        data-testid="escalation-save-button"
                        style={{
                          width: '100%', padding: '10px 16px', fontSize: 13, fontWeight: 600,
                          background: 'var(--primary)', color: '#fff', border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          cursor: (escalationSaving || Object.keys(escalationDayEdits).length === 0) ? 'not-allowed' : 'pointer',
                          opacity: (escalationSaving || Object.keys(escalationDayEdits).length === 0) ? 0.5 : 1,
                          marginTop: 8,
                        }}
                      >
                        {escalationSaving ? 'Saving…' : 'Save Escalation Settings'}
                      </button>
                    </>
                  )}
                  {escalationRules.length === 0 && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                      No escalation rules configured. They will be created when you next create a tenant or project.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Phase Configuration Defaults (CFG.A4) — hidden for solo
              plans (one project at a time, defaults overkill) per #779. */}
          {isAdmin && isSectionVisible('phase-config-defaults', tier) && <PhaseDefaultsSection cardStyle={cardStyle} />}

          {/* #2504 (RZP.C1) — Terms/Privacy links to the marketing-hosted
              canonical pages. DELIBERATELY a sibling of the sections above and
              NOT inside any isAdmin / isSectionVisible gate: legal links must be
              reachable by every authenticated user on this page, not just
              admins. If this ever moves inside a gate, the non-admin test in
              __tests__/LegalLinks.surfaces.test.tsx goes red. */}
          <LegalLinks style={{ marginTop: 32 }} />

        </div>
      </main>
    </div>
  );
}

// ── TemplateItems sub-component ───────────────────────────────────────────────

interface TemplateItemsProps {
  templateId: string;
}

interface SectionWithItems {
  id: string;
  title: string;
  order: number;
  items: { id: string; title: string; fieldType: string; isRequired: boolean; order: number }[];
}

function TemplateItems({ templateId }: TemplateItemsProps) {
  const [sections, setSections] = useState<SectionWithItems[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ sections: SectionWithItems[] }>(`/checklist-templates/${templateId}`)
      .then(r => setSections(r.sections ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [templateId]);

  if (loading) {
    return <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>Loading…</p>;
  }

  const FIELD_ICON: Record<string, string> = { CHECKBOX: '☐', SIGNOFF: '✍', TEXT: '✏', SELECT: '⌄' };

  return (
    <div style={{ marginTop: 8 }}>
      {sections.map(s => (
        <div key={s.id} style={{ marginBottom: 10 }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            {s.title}
          </p>
          {s.items.map(item => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', flexShrink: 0 }}>{FIELD_ICON[item.fieldType] ?? '☐'}</span>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)' }}>{item.title}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
